import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Coins, Download, FileBarChart, HandCoins, Printer, Truck } from 'lucide-react';
import { Button, PageHeader, Tabs } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/axios';
import { peutVoirTarifs } from '@/lib/permissions';
import { useAuthStore } from '@/store/authStore';
import { type EnginDTO, listEngins } from '@/services/engins';
import { openEtatLocationPdf } from '@/services/etatsLocation';
import { toast } from '@/store/toastStore';
import { LocationSortanteTab } from './LocationSortanteTab';
import { LocationsParLoueur } from './LocationsParLoueur';
import { FicheMensuelleModal } from './FicheMensuelleModal';

type Sens = 'sortante' | 'entrante';

/**
 * Location d'engins — point d'entrée unique du métier location, hors fiche machine.
 * Définitions canoniques, à ne jamais inverser :
 * - « Sortante » : MA machine (type PROPRE) louée à un CLIENT → il me paie → revenu.
 *   On choisit la machine, on pointe et on facture le client.
 * - « Entrante » : machine d'un TIERS que je prends en location pour mon chantier → je paie
 *   le loueur → dépense. Vue PAR LOUEUR : il possède N engins, tous pointés dans la même
 *   grille, et il reçoit UNE facture par chantier.
 */
export function LocationsEnginsPage() {
  const navigate = useNavigate();
  // La location sortante (contrats clients) est 100 % financière : réservée au droit « tarifs ».
  const voitTarifs = peutVoirTarifs(useAuthStore((s) => s.user));
  const [sens, setSens] = useState<Sens>(voitTarifs ? 'sortante' : 'entrante');
  const [enginId, setEnginId] = useState<string | null>(null);
  const [recherche, setRecherche] = useState('');
  const [ficheOuverte, setFicheOuverte] = useState(false);   // fiche de pointage mensuelle de la machine choisie

  const { data, isLoading } = useQuery({ queryKey: ['engins', 'locations'], queryFn: () => listEngins({}), enabled: sens === 'sortante' });

  // Machines éligibles à la location sortante : les miennes (PROPRE).
  const machines = useMemo<EnginDTO[]>(() => {
    const q = recherche.trim().toLowerCase();
    return (data?.items ?? [])
      .filter((e) => e.type === 'PROPRE')
      .filter((e) => !q || `${e.code} ${e.designation} ${e.marque ?? ''}`.toLowerCase().includes(q));
  }, [data, recherche]);

  const selected = machines.find((m) => m.id === enginId) ?? null;
  const changerSens = (s: Sens) => { setSens(s); setEnginId(null); };

  const imprimerEtatMachine = async (mode: 'open' | 'download') => {
    if (!selected) return;
    try {
      await openEtatLocationPdf({ granularite: 'mois', sens: 'sortante', engin_id: selected.id }, mode);
    } catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  return (
    <>
      <PageHeader
        title="Location d'engins"
        subtitle="SORTANTE : ma machine louée à un client, il me paie (revenu). ENTRANTE : machines d'un tiers que je loue pour mon chantier, je paie le loueur (dépense). Pointage, décompte et facturation."
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Parc & matériel', to: '/engins' }, { label: "Location d'engins" }]}
        actions={
          voitTarifs ? (
            <Button variant="secondary" onClick={() => navigate('/engins/etats')}>
              <FileBarChart /> État global (toutes machines)
            </Button>
          ) : undefined
        }
      />

      <Tabs
        tabs={[
          ...(voitTarifs ? [{ key: 'sortante', label: 'Sortante — mes machines louées à des clients', icon: <HandCoins className="size-4" /> }] : []),
          { key: 'entrante', label: 'Entrante — engins de tiers que je loue (par loueur)', icon: <Coins className="size-4" /> },
        ]}
        value={sens}
        onChange={(k) => changerSens(k as Sens)}
        className="mb-6"
      />

      {sens === 'entrante' ? (
        <LocationsParLoueur />
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr]">
          {/* Colonne gauche : choix de la machine */}
          <div className="space-y-2">
            <Input placeholder="Rechercher une machine…" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
            {isLoading ? (
              <p className="py-6 text-center text-sm text-content-muted">Chargement…</p>
            ) : machines.length === 0 ? (
              <p className="rounded-md border border-border bg-surface-secondary px-3 py-6 text-center text-xs text-content-muted">
                Aucune machine vous appartenant. Ajoutez-en une depuis « Parc & matériel → Nouveau ».
              </p>
            ) : machines.map((m) => (
              <button
                key={m.id}
                onClick={() => setEnginId(m.id)}
                className={cn('w-full rounded-md border px-3 py-2 text-left text-sm transition-colors',
                  enginId === m.id ? 'border-brand-primary bg-brand-primary/5' : 'border-border hover:bg-surface-hover')}
              >
                <span className="flex items-center gap-2">
                  <Truck className="size-4 shrink-0 text-content-muted" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-content-primary">{m.designation}</span>
                    <span className="block truncate text-[11px] text-content-muted">
                      {m.code}{m.immatriculation ? ` · ${m.immatriculation}` : ''}
                    </span>
                  </span>
                </span>
              </button>
            ))}
          </div>

          {/* Colonne droite : le dossier de location de la machine choisie */}
          <div>
            {!selected ? (
              <p className="py-16 text-center text-sm text-content-muted">
                Choisissez une machine à gauche pour pointer ses journées et établir son décompte.
              </p>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-surface-secondary px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-content-primary">{selected.designation}</p>
                    <p className="truncate text-xs text-content-muted">{selected.code} · ma machine, louée à un client — il me paie (revenu)</p>
                  </div>
                  {/* État PROPRE À CETTE MACHINE (le global reste accessible en haut). */}
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => setFicheOuverte(true)}>
                      <Printer className="size-4" /> Fiche de pointage mensuelle
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => imprimerEtatMachine('open')}>
                      <Printer className="size-4" /> État de cette machine
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => imprimerEtatMachine('download')} aria-label="Télécharger l'état de cette machine">
                      <Download className="size-4" />
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => navigate(`/engins/etats?engin_id=${selected.id}&sens=sortante`)}>
                      <FileBarChart className="size-4" /> Calendrier détaillé
                    </Button>
                  </div>
                </div>
                <LocationSortanteTab key={selected.id} enginId={selected.id} />
              </div>
            )}
          </div>
        </div>
      )}
      {ficheOuverte && selected && (
        <FicheMensuelleModal titre={`${selected.code} · ${selected.designation}`} perimetre={{ engin_id: selected.id }}
          moisInitial={new Date()} onClose={() => setFicheOuverte(false)} />
      )}
    </>
  );
}
